import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { catalogsReady, getLoadedCatalog, loadLocaleCatalogsWithRetry, routeCatalogFeatures } from "./catalogLoader";
import type { CatalogFeature } from "./catalogLoader";
import { loadingMessages } from "./catalogs/essential";
import PageLoadingIndicator from "../components/PageLoadingIndicator";
import type { LocaleCode, LocaleDefinition } from "./types";
import localeDefinitionsData from "../content/locales.json";
import { refreshedCopyAliases } from "./copyAliases";
import { readUrlLocale } from "./publicRoutes";

export type { LocaleCode, LocaleDefinition, TranslationDictionary } from "./types";

type ReplacementValue = string | number | boolean | null | undefined;
type TranslationReplacements = Record<string, ReplacementValue>;

type LocalizationContextValue = {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  supportedLocales: LocaleDefinition[];
  t: (source: string, replacements?: TranslationReplacements) => string;
  prepareFeatures: (features: readonly CatalogFeature[]) => Promise<void>;
  catalogVersion: number;
};

type TextNodeRecord = {
  original: string;
  lastRendered: string;
};

type AttributeRecord = {
  original: string;
  lastRendered: string;
};

const LOCALE_STORAGE_KEY = "rauxa.locale";
const LEGACY_LOCALE_STORAGE_KEY = "corsa.locale";
const DEFAULT_LOCALE: LocaleCode = "en";
const TRANSLATED_ATTRIBUTES = ["aria-label", "title", "placeholder", "alt"];
const SKIP_TEXT_LOCALIZATION_SELECTOR =
  "script, style, code, pre, kbd, samp, svg, textarea, [data-i18n-skip]";
const SKIP_ATTRIBUTE_LOCALIZATION_SELECTOR =
  "script, style, code, pre, kbd, samp, svg, [data-i18n-skip]";

const supportedLocales = localeDefinitionsData as LocaleDefinition[];

const localeDefinitions = new Map(
  supportedLocales.map((localeDefinition) => [localeDefinition.code, localeDefinition])
);

const textNodeRecords = new WeakMap<Text, TextNodeRecord>();
const attributeRecords = new WeakMap<Element, Record<string, AttributeRecord>>();

let activeLocale: LocaleCode = DEFAULT_LOCALE;

const isLocaleCode = (value: string | null | undefined): value is LocaleCode =>
  Boolean(value && localeDefinitions.has(value as LocaleCode));

const persistLocale = (locale: LocaleCode) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Locale persistence is best-effort.
  }
};

const migrateLegacyLocale = (locale: LocaleCode) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    window.localStorage.removeItem(LEGACY_LOCALE_STORAGE_KEY);
  } catch {
    // Locale migration is best-effort.
  }
};

const matchSupportedLocale = (value: string | null | undefined): LocaleCode | null => {
  const normalizedValue = value?.trim().replace(/_/g, "-").toLowerCase();
  if (!normalizedValue) {
    return null;
  }

  const exactMatch = supportedLocales.find((localeDefinition) =>
    [
      localeDefinition.code,
      localeDefinition.htmlLang,
      localeDefinition.intlLocale,
    ]
      .map((candidate) => candidate.toLowerCase())
      .includes(normalizedValue)
  );
  if (exactMatch) {
    return exactMatch.code;
  }

  const languageCode = normalizedValue.split("-")[0];
  const languageMatch = supportedLocales.find(
    (localeDefinition) =>
      localeDefinition.code.toLowerCase().split("-")[0] === languageCode
  );
  return languageMatch?.code || null;
};

const detectNavigatorLocale = (): LocaleCode | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const languageCandidates = [
    ...(Array.isArray(window.navigator.languages) ? window.navigator.languages : []),
    window.navigator.language,
  ];

  for (const languageCandidate of languageCandidates) {
    const locale = matchSupportedLocale(languageCandidate);
    if (locale) {
      return locale;
    }
  }

  return null;
};

export const resolveInitialLocale = (): LocaleCode => {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  const urlLocale = readUrlLocale(window.location.search);
  if (urlLocale) return urlLocale;

  try {
    const storedLocale = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocaleCode(storedLocale)) {
      return storedLocale;
    }

    const legacyLocale = window.localStorage.getItem(LEGACY_LOCALE_STORAGE_KEY);
    if (isLocaleCode(legacyLocale)) {
      migrateLegacyLocale(legacyLocale);
      return legacyLocale;
    }
  } catch {
    // Browser language detection still works when storage is unavailable.
  }

  return detectNavigatorLocale() || DEFAULT_LOCALE;
};

const normalizeSource = (value: string): string =>
  value.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();

const withOriginalWhitespace = (original: string, translated: string): string => {
  const leadingWhitespace = original.match(/^\s*/)?.[0] || "";
  const trailingWhitespace = original.match(/\s*$/)?.[0] || "";
  return `${leadingWhitespace}${translated}${trailingWhitespace}`;
};

const interpolate = (
  source: string,
  replacements?: TranslationReplacements
): string => {
  if (!replacements) {
    return source;
  }

  return source.replace(/\{([A-Za-z0-9_]+)\}/g, (match, key: string) => {
    const replacementValue = replacements[key];
    if (replacementValue === null || replacementValue === undefined) {
      return match;
    }

    return String(replacementValue);
  });
};

export const getLocaleDefinition = (locale: LocaleCode = activeLocale): LocaleDefinition => {
  const definition = localeDefinitions.get(locale) || localeDefinitions.get(DEFAULT_LOCALE)!;
  return { ...definition, ...getLoadedCatalog(definition.code) };
};

export const getCurrentLocale = (): LocaleCode => activeLocale;

export const getCurrentIntlLocale = (): string => getLocaleDefinition().intlLocale;

export const translateText = (
  source: string,
  locale: LocaleCode = activeLocale,
  replacements?: TranslationReplacements
): string => {
  const normalizedSource = normalizeSource(source);
  if (!normalizedSource) {
    return source;
  }

  const localeDefinition = localeDefinitions.get(locale) || localeDefinitions.get(DEFAULT_LOCALE)!;
  if (localeDefinition.code === "en") {
    return interpolate(normalizedSource, replacements);
  }

  const catalog = getLoadedCatalog(localeDefinition.code);
  const translationSource = refreshedCopyAliases[normalizedSource];
  const exactTranslation =
    catalog?.translations?.[normalizedSource] ||
    (translationSource ? catalog?.translations?.[translationSource] : undefined);
  if (exactTranslation) {
    return interpolate(exactTranslation, replacements);
  }

  const patternTranslation = catalog?.patterns
    ?.map((translationPattern) => {
      const match = normalizedSource.match(translationPattern.pattern);
      return match ? translationPattern.translate(match) : null;
    })
    .find((value): value is string => Boolean(value));

  return interpolate(patternTranslation || normalizedSource, replacements);
};

const shouldSkipTextLocalization = (element: Element | null): boolean =>
  Boolean(element?.closest(SKIP_TEXT_LOCALIZATION_SELECTOR));

const shouldSkipAttributeLocalization = (element: Element | null): boolean =>
  Boolean(element?.closest(SKIP_ATTRIBUTE_LOCALIZATION_SELECTOR));

const localizeTextNode = (node: Text, locale: LocaleCode) => {
  if (!node.parentElement || shouldSkipTextLocalization(node.parentElement)) {
    return;
  }

  const currentValue = node.data;
  if (!normalizeSource(currentValue)) {
    return;
  }

  let record = textNodeRecords.get(node);
  if (!record || currentValue !== record.lastRendered) {
    record = {
      original: currentValue,
      lastRendered: currentValue,
    };
    textNodeRecords.set(node, record);
  }

  const translatedValue = withOriginalWhitespace(
    record.original,
    translateText(record.original, locale)
  );
  record.lastRendered = translatedValue;

  if (currentValue !== translatedValue) {
    node.data = translatedValue;
  }
};

const localizeElementAttributes = (element: Element, locale: LocaleCode, attributes = TRANSLATED_ATTRIBUTES) => {
  if (shouldSkipAttributeLocalization(element)) {
    return;
  }

  let records = attributeRecords.get(element);
  if (!records) {
    records = {};
    attributeRecords.set(element, records);
  }
  const elementRecords = records;

  attributes.forEach((attributeName) => {
    const currentValue = element.getAttribute(attributeName);
    if (!currentValue || !normalizeSource(currentValue)) {
      return;
    }

    let record = elementRecords[attributeName];
    if (!record || currentValue !== record.lastRendered) {
      record = {
        original: currentValue,
        lastRendered: currentValue,
      };
      elementRecords[attributeName] = record;
    }

    const translatedValue = translateText(record.original, locale);
    record.lastRendered = translatedValue;

    if (currentValue !== translatedValue) {
      element.setAttribute(attributeName, translatedValue);
    }
  });
};

const localizeDomTree = (root: Node, locale: LocaleCode) => {
  if (root instanceof Text) {
    localizeTextNode(root, locale);
    return;
  }
  if (root instanceof Element) {
    if (shouldSkipAttributeLocalization(root)) return;
    localizeElementAttributes(root, locale);
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => node instanceof Element && shouldSkipAttributeLocalization(node)
      ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
  });
  let node = walker.nextNode();
  while (node) {
    if (node instanceof Text) localizeTextNode(node, locale);
    else if (node instanceof Element) localizeElementAttributes(node, locale);
    node = walker.nextNode();
  }
};

const localizeMutations = (mutations: MutationRecord[], locale: LocaleCode) => {
  const addedRoots = new Set<Node>();
  const textNodes = new Set<Text>();
  const attributes = new Map<Element, Set<string>>();
  for (const mutation of mutations) {
    if (mutation.type === "childList") {
      mutation.addedNodes.forEach((node) => {
        if (node.isConnected && !(node instanceof Element && shouldSkipAttributeLocalization(node)) &&
          !(node instanceof Text && shouldSkipTextLocalization(node.parentElement))) addedRoots.add(node);
      });
    } else if (mutation.type === "characterData" && mutation.target instanceof Text) {
      const node = mutation.target;
      if (node.isConnected && !shouldSkipTextLocalization(node.parentElement) &&
        textNodeRecords.get(node)?.lastRendered !== node.data) textNodes.add(node);
    } else if (mutation.type === "attributes" && mutation.target instanceof Element && mutation.attributeName) {
      const element = mutation.target;
      const name = mutation.attributeName;
      if (!element.isConnected || shouldSkipAttributeLocalization(element)) continue;
      const currentValue = element.getAttribute(name);
      if (currentValue === null) {
        const records = attributeRecords.get(element);
        if (records) delete records[name];
      } else if (attributeRecords.get(element)?.[name]?.lastRendered !== currentValue) {
        if (!attributes.has(element)) attributes.set(element, new Set());
        attributes.get(element)!.add(name);
      }
    }
  }
  // Keep only the highest added root. Its single traversal covers all descendants.
  const roots = Array.from(addedRoots).filter((node) => {
    let parent = node.parentNode;
    while (parent) {
      if (addedRoots.has(parent)) return false;
      parent = parent.parentNode;
    }
    return true;
  });
  const coveredByRoot = (node: Node) => roots.some((root) => root === node || root.contains(node));
  roots.forEach((root) => localizeDomTree(root, locale));
  textNodes.forEach((node) => { if (!coveredByRoot(node)) localizeTextNode(node, locale); });
  attributes.forEach((names, element) => {
    if (!coveredByRoot(element)) localizeElementAttributes(element, locale, Array.from(names));
  });
};

const DomLocalizer = ({ locale, catalogVersion }: { locale: LocaleCode; catalogVersion: number }) => {
  useLayoutEffect(() => {
    if (typeof document === "undefined" || !document.body) return undefined;
    const localeDefinition = getLocaleDefinition(locale);
    document.documentElement.lang = localeDefinition.htmlLang;
    document.documentElement.dir = localeDefinition.textDirection;
    localizeDomTree(document.body, locale);
    // MutationObserver already batches a commit's mutations. Apply before the next
    // paint, including lazy-route content and portals, without a full-body scan.
    const observer = new MutationObserver((mutations) => localizeMutations(mutations, locale));
    observer.observe(document.body, {
      attributeFilter: TRANSLATED_ATTRIBUTES,
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [locale, catalogVersion]);
  return null;
};

const LocalizationContext = createContext<LocalizationContextValue | null>(null);

const initialFeatures = (): CatalogFeature[] => routeCatalogFeatures(
  typeof window === "undefined" ? "/" : window.location.pathname
);

export const LocalizationProvider = ({ children }: { children: React.ReactNode }) => {
  const [locale, setLocaleState] = useState<LocaleCode>(() => {
    const initialLocale = resolveInitialLocale();
    activeLocale = initialLocale;
    return initialLocale;
  });
  const requiredFeatures = useRef(new Set<CatalogFeature>(initialFeatures()));
  const [initialized, setInitialized] = useState(() => catalogsReady(locale, initialFeatures()));
  const [catalogVersion, setCatalogVersion] = useState(0);
  const selectionGeneration = useRef(0);

  const prepareFeatures = useCallback(async (features: readonly CatalogFeature[]) => {
    features.forEach((feature) => requiredFeatures.current.add(feature));
    if (!catalogsReady(locale, features)) {
      await loadLocaleCatalogsWithRetry(locale, features);
      setCatalogVersion((version) => version + 1);
    }
  }, [locale]);

  const setLocale = useCallback((nextLocale: LocaleCode) => {
    if (!isLocaleCode(nextLocale)) return;
    const generation = ++selectionGeneration.current;
    const apply = () => {
      if (generation !== selectionGeneration.current) return;
      activeLocale = nextLocale;
      persistLocale(nextLocale);
      setLocaleState(nextLocale);
    };
    if (catalogsReady(nextLocale, Array.from(requiredFeatures.current))) {
      apply();
      return;
    }
    // Keep the current UI/form state mounted until all required translations exist.
    const prepare = async () => {
      do {
        await loadLocaleCatalogsWithRetry(nextLocale, Array.from(requiredFeatures.current));
      } while (generation === selectionGeneration.current &&
        !catalogsReady(nextLocale, Array.from(requiredFeatures.current)));
      apply();
    };
    void prepare().catch(() => {
      // Failed chunks are not cached. The language control can retry immediately.
    });
  }, []);

  useEffect(() => () => { selectionGeneration.current += 1; }, []);

  useEffect(() => {
    activeLocale = locale;
    persistLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (initialized) return undefined;
    let active = true;
    const prepare = () => {
      void loadLocaleCatalogsWithRetry(locale, Array.from(requiredFeatures.current)).then(() => {
        if (active) setInitialized(true);
      }).catch(() => {
        // Retry a failed initial chunk when connectivity or window focus returns.
      });
    };
    prepare();
    window.addEventListener("online", prepare);
    window.addEventListener("focus", prepare);
    return () => {
      active = false;
      window.removeEventListener("online", prepare);
      window.removeEventListener("focus", prepare);
    };
  }, [initialized, locale]);

  const contextValue = useMemo<LocalizationContextValue>(() => ({
    locale,
    setLocale,
    supportedLocales,
    prepareFeatures,
    catalogVersion,
    t: (source, replacements) => translateText(source, locale, replacements),
  }), [locale, setLocale, prepareFeatures, catalogVersion]);

  return (
    <LocalizationContext.Provider value={contextValue}>
      {initialized ? children : <PageLoadingIndicator className="app-route-loading" message={loadingMessages[locale]} />}
      {initialized ? <DomLocalizer locale={locale} catalogVersion={catalogVersion} /> : null}
    </LocalizationContext.Provider>
  );
};

// Must be inside the router and outside route content. In particular, a direct
// deep link must never render before its selected-language feature is loaded.
export const LocalizationRouteBoundary = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const { locale, prepareFeatures } = useLocalization();
  const features = useMemo(() => routeCatalogFeatures(pathname), [pathname]);
  const ready = catalogsReady(locale, features);
  useLayoutEffect(() => {
    const prepare = () => { void prepareFeatures(features).catch(() => {}); };
    prepare();
    window.addEventListener("online", prepare);
    window.addEventListener("focus", prepare);
    return () => {
      window.removeEventListener("online", prepare);
      window.removeEventListener("focus", prepare);
    };
  }, [features, prepareFeatures]);
  return ready ? <>{children}</> : <PageLoadingIndicator className="app-route-loading" message={loadingMessages[locale]} />;
};

export const useLocalization = (): LocalizationContextValue => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error("useLocalization must be used within LocalizationProvider.");
  }

  return context;
};

export const LanguageSwitcher = () => {
  const { locale, setLocale, supportedLocales: locales, t } = useLocalization();

  return (
    <label className="language-switcher">
      <span>{t("Language")}</span>
      <select
        aria-label={t("Language")}
        data-i18n-skip
        value={locale}
        onChange={(event) => setLocale(event.target.value as LocaleCode)}
      >
        {locales.map((localeDefinition) => (
          <option key={localeDefinition.code} value={localeDefinition.code}>
            {localeDefinition.nativeLabel}
          </option>
        ))}
      </select>
    </label>
  );
};

type LanguageDropdownProps = {
  className?: string;
};

const LocaleFlag = ({ definition }: { definition: LocaleDefinition }) =>
  definition.flagImage ? (
    <img src={definition.flagImage} alt="" width="16" height="16" />
  ) : (
    <>{definition.flagIcon}</>
  );

export const LanguageDropdown = ({ className = "" }: LanguageDropdownProps) => {
  const { locale, setLocale, supportedLocales: locales, t } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const optionsId = useId();
  const currentLocale = getLocaleDefinition(locale);
  const classNames = ["language-menu", className].filter(Boolean).join(" ");

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current) {
        return;
      }

      if (event.target instanceof Node && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen((current) => {
          if (current) {
            triggerRef.current?.focus();
          }
          return false;
        });
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className={classNames} ref={menuRef} data-i18n-skip>
      <button
        ref={triggerRef}
        className="language-menu-trigger"
        type="button"
        aria-expanded={isOpen}
        aria-controls={optionsId}
        aria-label={t("Language")}
        title={t("Language")}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="language-menu-flag" aria-hidden="true">
          <LocaleFlag definition={currentLocale} />
        </span>
        <span className="language-menu-code">{currentLocale.code.toUpperCase()}</span>
      </button>

      <div
        id={optionsId}
        className={`language-menu-dropdown${isOpen ? " is-open" : ""}`}
        aria-label={t("Language")}
        hidden={!isOpen}
      >
        {locales.map((localeDefinition) => (
          <button
            key={localeDefinition.code}
            className={`language-menu-item${
              localeDefinition.code === locale ? " is-selected" : ""
            }`}
            type="button"
            aria-pressed={localeDefinition.code === locale}
            onClick={() => {
              setLocale(localeDefinition.code);
              setIsOpen(false);
              triggerRef.current?.focus();
            }}
          >
            <span className="language-menu-item-flag" aria-hidden="true">
              <LocaleFlag definition={localeDefinition} />
            </span>
            <span className="language-menu-item-label">
              {localeDefinition.nativeLabel}
            </span>
            <span className="language-menu-item-code">
              {localeDefinition.code.toUpperCase()}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
